/**
 * Purchases Builder — config card + subscriber results panel.
 */
import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { format, parseISO } from 'date-fns';
import { getSectionCache, setSectionCache } from '@/lib/sectionCache';
import { useSavedReport } from '@/hooks/useSavedReport';
import { KpiCard } from '@/components/ui/KpiCard';
import { Pagination } from '@/components/ui/Pagination';
import {
  Play,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Users,
  DollarSign,
  CreditCard,
  TrendingUp,
  Activity,
  ArrowUpRight,
  RefreshCw,
  XCircle,
  Timer,
  ShieldAlert,
  Monitor,
  Signal,
  Clock,
  Layers,
} from 'lucide-react';
import { cn } from '@/lib/cn';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { DatePresetPicker } from '@/components/filters/DatePresetPicker';
import { FilterBuilder } from '@/components/filters/FilterBuilder';
import { BuilderShell } from '@/components/builder/BuilderShell';
import { api } from '@/services/api';
import { endpoints } from '@/services/endpoints';
import { toast } from '@/components/ui/Toast';
import { usePurchasesState } from './usePurchasesState';
import type { PurchasesState } from './usePurchasesState';
import {
  PURCHASES_FILTER_FIELDS,
  PURCHASES_FILTER_OPERATORS,
  PURCHASES_FILTER_FIELD_META,
} from './purchases.constants';

// ─── Types ──────────────────────────────────────────────────────────────────

interface SubscriberRow {
  user_id: string | null;
  user_pseudo_id: string;
  platform: string | null;
  country: string | null;
  city: string | null;
  app_version: string | null;
  device_brand: string | null;
  device_model: string | null;
  status: 'active' | 'cancelled' | 'expired' | 'billing_issue' | 'unknown';
  last_event_name: string;
  offering_id: string | null;
  subscription_period: string | null;
  currency: string | null;
  purchase_structure: string | null;
  total_revenue_try: number;
  last_price_try: number | null;
  renewal_count: number;
  event_time: string | null;
  event_timestamp: number;
  campaign_name: string | null;
  traffic_source_name: string | null;
  ad_group_name: string | null;
  traffic_source: string | null;
  is_web_to_app_user: boolean | null;
  retention_days: number | null;
  device_id: string | null;
}

interface PurchasesSummary {
  totalSubscribers: number;
  activeCount: number;
  cancelledCount: number;
  expiredCount: number;
  billingIssueCount: number;
  totalRevenueTry: number;
  avgRevenueTry: number;
  newPurchasesToday: number;
  renewalsToday: number;
  platformBreakdown: Record<string, number>;
  topCountries: Array<{ country: string; count: number }>;
}

interface PurchaseEvent {
  event_name: string;
  event_timestamp: number;
  event_time: string | null;
  price_in_try: number | null;
  price_in_usd: number | null;
  transaction_id: string | null;
  offering_id: string | null;
  subscription_period: string | null;
  currency: string | null;
  renewal_count: number | null;
  purchase_structure: string | null;
}

interface SessionRow {
  user_pseudo_id: string;
  user_id: string | null;
  event_session_id: string;
  session_start_event_name: string | null;
  session_start_timestamp: number;
  session_start_time: { value: string } | string;
  session_end_timestamp: number;
  session_end_time: { value: string } | string;
  session_duration_seconds: number | null;
  event_count: number;
}

interface SessionEvent {
  event_name: string;
  event_timestamp: number;
  event_time: { value: string } | string;
  params: Array<{
    key: string;
    string_value: string | null;
    int_value: number | null;
    double_value: number | null;
  }> | null;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

const COUNTRY_CODES: Record<string, string> = {
  'Afghanistan': 'af', 'Albania': 'al', 'Algeria': 'dz', 'Argentina': 'ar',
  'Armenia': 'am', 'Australia': 'au', 'Austria': 'at', 'Azerbaijan': 'az',
  'Bahrain': 'bh', 'Bangladesh': 'bd', 'Belarus': 'by', 'Belgium': 'be',
  'Bolivia': 'bo', 'Bosnia & Herzegovina': 'ba', 'Brazil': 'br', 'Bulgaria': 'bg',
  'Cambodia': 'kh', 'Cameroon': 'cm', 'Canada': 'ca', 'Chile': 'cl',
  'China': 'cn', 'Colombia': 'co', 'Costa Rica': 'cr', 'Croatia': 'hr',
  'Czechia': 'cz', 'Denmark': 'dk', 'Dominican Republic': 'do', 'Ecuador': 'ec',
  'Egypt': 'eg', 'El Salvador': 'sv', 'Estonia': 'ee', 'Ethiopia': 'et',
  'Finland': 'fi', 'France': 'fr', 'Georgia': 'ge', 'Germany': 'de',
  'Ghana': 'gh', 'Greece': 'gr', 'Guatemala': 'gt', 'Honduras': 'hn',
  'Hong Kong': 'hk', 'Hungary': 'hu', 'Iceland': 'is', 'India': 'in',
  'Indonesia': 'id', 'Iran': 'ir', 'Iraq': 'iq', 'Ireland': 'ie',
  'Israel': 'il', 'Italy': 'it', 'Jamaica': 'jm', 'Japan': 'jp',
  'Jordan': 'jo', 'Kazakhstan': 'kz', 'Kenya': 'ke', 'Kuwait': 'kw',
  'Kyrgyzstan': 'kg', 'Laos': 'la', 'Latvia': 'lv', 'Lebanon': 'lb',
  'Libya': 'ly', 'Lithuania': 'lt', 'Luxembourg': 'lu', 'Malaysia': 'my',
  'Mexico': 'mx', 'Moldova': 'md', 'Mongolia': 'mn', 'Morocco': 'ma',
  'Myanmar (Burma)': 'mm', 'Nepal': 'np', 'Netherlands': 'nl', 'New Zealand': 'nz',
  'Nicaragua': 'ni', 'Nigeria': 'ng', 'North Macedonia': 'mk', 'Norway': 'no',
  'Oman': 'om', 'Pakistan': 'pk', 'Palestine': 'ps', 'Panama': 'pa',
  'Paraguay': 'py', 'Peru': 'pe', 'Philippines': 'ph', 'Poland': 'pl',
  'Portugal': 'pt', 'Puerto Rico': 'pr', 'Qatar': 'qa', 'Romania': 'ro',
  'Russia': 'ru', 'Saudi Arabia': 'sa', 'Senegal': 'sn', 'Serbia': 'rs',
  'Singapore': 'sg', 'Slovakia': 'sk', 'Slovenia': 'si', 'South Africa': 'za',
  'South Korea': 'kr', 'Spain': 'es', 'Sri Lanka': 'lk', 'Sudan': 'sd',
  'Sweden': 'se', 'Switzerland': 'ch', 'Syria': 'sy', 'Taiwan': 'tw',
  'Tanzania': 'tz', 'Thailand': 'th', 'Tunisia': 'tn', 'Turkey': 'tr',
  'Türkiye': 'tr', 'Uganda': 'ug', 'Ukraine': 'ua', 'United Arab Emirates': 'ae',
  'United Kingdom': 'gb', 'United States': 'us', 'Uruguay': 'uy',
  'Uzbekistan': 'uz', 'Venezuela': 've', 'Vietnam': 'vn', 'Yemen': 'ye',
  'Zambia': 'zm', 'Zimbabwe': 'zw', 'Malta': 'mt', 'Montenegro': 'me',
};

function flagUrl(country: string | null): string {
  if (!country) return 'https://flagcdn.com/w40/xx.png';
  const code = COUNTRY_CODES[country];
  return code ? `https://flagcdn.com/w40/${code}.png` : 'https://flagcdn.com/w40/xx.png';
}

function platformIconSrc(platform: string | null): string {
  if (platform === 'IOS') return '/icons/apple.svg';
  if (platform === 'ANDROID') return '/icons/android.svg';
  return '';
}

function formatEventTime(ts: string | null): string {
  if (!ts) return '-';
  try { return format(parseISO(ts), 'HH:mm'); } catch { return '-'; }
}

function formatDateTime(ts: string | null): string {
  if (!ts) return '-';
  try { return format(parseISO(ts), 'MMM d, HH:mm:ss'); } catch { return '-'; }
}

function commaFmt(n: number): string {
  return n.toLocaleString();
}

function currencyFmt(n: number): string {
  return `₺${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// RevenueCat renewal_count is the billing period number (1 = initial purchase, 2 = 1st renewal, etc.)
// Actual renewals = renewal_count - 1
function actualRenewals(renewalCount: number): number {
  return Math.max(renewalCount - 1, 0);
}

function eventBadgeConfig(lastEventName: string, renewalCount: number): { label: string; variant: 'success' | 'warning' | 'danger' | 'default' | 'brand' } {
  const renewals = actualRenewals(renewalCount);
  switch (lastEventName) {
    case 'initial_purchase': return { label: 'Initial Purchase', variant: 'success' };
    case 'renewal': return { label: renewals > 0 ? `Renewal #${renewals}` : 'Renewal', variant: 'brand' };
    case 'subscription_extended': return { label: 'Extended', variant: 'success' };
    case 'cancellation': return { label: 'Cancelled', variant: 'warning' };
    case 'expiration': return { label: 'Expired', variant: 'danger' };
    case 'billing_issue': return { label: 'Billing Issue', variant: 'danger' };
    default: return { label: lastEventName?.replace(/_/g, ' ') || 'Unknown', variant: 'default' };
  }
}

function periodLabel(period: string | null): string {
  if (!period) return '-';
  const map: Record<string, string> = { P1W: 'Weekly', weekly: 'Weekly', P1M: 'Monthly', monthly: 'Monthly', P3M: 'Quarterly', P6M: 'Semi-Annual', P1Y: 'Yearly', yearly: 'Yearly' };
  return map[period] ?? period;
}

function structureLabel(s: string | null): string {
  if (!s) return '';
  return s.replace(/_/g, ' ');
}

function parseBqTimestamp(ts: { value: string } | string | null): string {
  if (!ts) return '-';
  const raw = typeof ts === 'object' && 'value' in ts ? ts.value : ts;
  try { return format(parseISO(raw), 'HH:mm:ss'); } catch { return raw; }
}

function parseBqDateTime(ts: { value: string } | string | null): string {
  if (!ts) return '-';
  const raw = typeof ts === 'object' && 'value' in ts ? ts.value : ts;
  try { return format(parseISO(raw), 'MMM d, HH:mm'); } catch { return raw; }
}

function formatSessionDuration(seconds: number | null): string {
  if (seconds == null) return '-';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return `${m}m ${s}s`;
}

const SESSION_EVENT_COLORS: Record<string, string> = {
  app_launch: '#0EA5E9',
  app_first_opened: '#10B981',
  purchase_success: '#F59E0B',
  purchase_started: '#F97316',
  purchase_failed: '#EF4444',
  paywall_opened: '#8B5CF6',
  paywall_closed: '#A78BFA',
  push_authorized: '#10B981',
  push_unauthorized: '#EF4444',
  app_backgrounded: '#94A3B8',
  app_resumed: '#64748B',
  onboarding_completed: '#10B981',
};

function getSessionEventColor(name: string): string {
  if (SESSION_EVENT_COLORS[name]) return SESSION_EVENT_COLORS[name];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return `hsl(${Math.abs(hash) % 360}, 55%, 50%)`;
}

// ─── Cache ──────────────────────────────────────────────────────────────────

interface PurchasesCacheData {
  subscribers: SubscriberRow[];
  summary: PurchasesSummary | null;
  total: number;
  totalPages: number;
}

const CACHE_KEY = 'purchases-builder';

// ─── ColorChip ──────────────────────────────────────────────────────────────

function ColorChip({ value, muted }: { value: string; muted?: boolean }) {
  let hash = 0;
  for (let i = 0; i < value.length; i++) hash = value.charCodeAt(i) + ((hash << 5) - hash);
  const hue = Math.abs(hash) % 360;
  const sat = muted ? 40 : 65;
  const bg = `hsl(${hue}, ${sat}%, 95%)`;
  const text = `hsl(${hue}, ${sat - 5}%, 35%)`;
  const border = `hsl(${hue}, ${sat - 15}%, 85%)`;
  return (
    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-2xs font-medium whitespace-nowrap flex-shrink-0" style={{ background: bg, color: text, border: `1px solid ${border}` }} title={value}>
      {value}
    </span>
  );
}

// ─── Main Component ─────────────────────────────────────────────────────────

const SECTION_ID = 'purchases';

export function PurchasesBuilder() {
  const { reportId } = useParams();
  const { state, update, reset, dates, toApiParams, toConfig, filterCount, searchParams } = usePurchasesState();

  const cached = useRef(getSectionCache<PurchasesCacheData>(CACHE_KEY));

  const [subscribers, setSubscribers] = useState<SubscriberRow[]>(cached.current?.subscribers ?? []);
  const [summary, setSummary] = useState<PurchasesSummary | null>(cached.current?.summary ?? null);
  const [total, setTotal] = useState(cached.current?.total ?? 0);
  const [totalPages, setTotalPages] = useState(cached.current?.totalPages ?? 1);
  const [loading, setLoading] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [hasRun, setHasRun] = useState(!!cached.current);

  const dataRef = useRef<PurchasesCacheData>({ subscribers, summary, total, totalPages });
  const hasRunRef = useRef(hasRun);
  useEffect(() => { dataRef.current = { subscribers, summary, total, totalPages }; }, [subscribers, summary, total, totalPages]);
  useEffect(() => { hasRunRef.current = hasRun; }, [hasRun]);
  useEffect(() => {
    return () => { if (hasRunRef.current) setSectionCache(CACHE_KEY, dataRef.current); };
  }, []);

  const [selectedSubscriber, setSelectedSubscriber] = useState<SubscriberRow | null>(null);

  // Load saved report
  const { savedReport } = useSavedReport<PurchasesState>({
    reportId,
    sectionId: SECTION_ID,
    searchParams,
    update,
    mapConfig: useCallback((config: Record<string, unknown>) => ({
      preset: (config.preset as string) || 'today',
      start: (config.start as string) || '',
      end: (config.end as string) || '',
      filters: (config.filters as string) || '',
    }), []),
  });

  const configAppliedRef = useRef(!!savedReport);

  // Run
  const runQuery = useCallback(async () => {
    setLoading(true);
    setSummaryLoading(true);
    setHasRun(true);
    setSelectedSubscriber(null);
    try {
      const params = toApiParams();
      const [listRes, summaryRes] = await Promise.all([
        api.getWithMeta<SubscriberRow[]>(endpoints.purchases.list(), params),
        api.get<PurchasesSummary>(endpoints.purchases.summary(), { startDate: dates.startDate, endDate: dates.endDate }),
      ]);
      setSubscribers(listRes.data ?? []);
      if (listRes.meta) {
        setTotal(listRes.meta.total as number);
        setTotalPages(listRes.meta.totalPages as number);
      }
      setSummary(summaryRes);
    } catch (err) {
      console.error('Failed to fetch purchases:', err);
      toast.error('Failed to load purchase data');
    } finally {
      setLoading(false);
      setSummaryLoading(false);
    }
  }, [toApiParams, dates]);

  // Pagination
  const prevPageRef = useRef(state.page);
  useEffect(() => {
    if (hasRun && state.page !== prevPageRef.current) {
      prevPageRef.current = state.page;
      setLoading(true);
      const params = toApiParams();
      api
        .getWithMeta<SubscriberRow[]>(endpoints.purchases.list(), params)
        .then((res) => {
          setSubscribers(res.data ?? []);
          if (res.meta) {
            setTotal(res.meta.total as number);
            setTotalPages(res.meta.totalPages as number);
          }
        })
        .catch(console.error)
        .finally(() => setLoading(false));
    }
  }, [state.page, hasRun, toApiParams]);

  // Auto-run saved reports
  useEffect(() => {
    if (savedReport && !hasRun) runQuery();
  }, [savedReport, hasRun, runQuery]);

  const configContent = (
    <div className="p-4 space-y-4">
      <DatePresetPicker value={state.preset} onChange={(preset: string) => update({ preset })} startDate={state.start} endDate={state.end} onDateChange={(start: string, end: string) => update({ start, end, preset: 'custom' })} />
      <div className="border border-slate-200 rounded-lg px-3 py-2.5">
        <FilterBuilder value={state.filters} onChange={(filters) => update({ filters })} fields={PURCHASES_FILTER_FIELDS} operators={PURCHASES_FILTER_OPERATORS} fieldMeta={PURCHASES_FILTER_FIELD_META} />
      </div>
    </div>
  );

  return (
    <BuilderShell
      title="Purchases"
      subtitle={savedReport?.report?.description || 'Subscription revenue and lifecycle'}
      sectionId={SECTION_ID}
      sectionRoute="/purchases"
      reportId={reportId}
      savedReport={savedReport as { report?: { name?: string; description?: string } } | undefined}
      onReset={reset}
      onRun={runQuery}
      isRunning={loading}
      configForSave={toConfig()}
      configPanel={configContent}
    >
      {!hasRun ? (
        <Card><EmptyState icon={Play} title="Configure and run" description="Set your date range and filters above, then click Run to load purchase data" /></Card>
      ) : selectedSubscriber ? (
        <SubscriberDetailPanel subscriber={selectedSubscriber} onBack={() => setSelectedSubscriber(null)} />
      ) : (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            <KpiCard label="Subscribers" value={summary ? commaFmt(summary.totalSubscribers) : null} icon={<Users className="w-4 h-4" />} color="#0EA5E9" loading={summaryLoading} />
            <KpiCard label="Active" value={summary ? commaFmt(summary.activeCount) : null} icon={<TrendingUp className="w-4 h-4" />} color="#10B981" loading={summaryLoading} />
            <KpiCard label="Revenue (TRY)" value={summary ? currencyFmt(summary.totalRevenueTry) : null} icon={<DollarSign className="w-4 h-4" />} color="#F59E0B" loading={summaryLoading} />
            <KpiCard label="New Purchases" value={summary ? commaFmt(summary.newPurchasesToday) : null} icon={<CreditCard className="w-4 h-4" />} color="#8B5CF6" loading={summaryLoading} />
            <KpiCard label="Renewals" value={summary ? commaFmt(summary.renewalsToday) : null} icon={<RefreshCw className="w-4 h-4" />} color="#EC4899" loading={summaryLoading} />
          </div>

          {!loading && <div className="text-xs text-slate-500">Showing <span className="font-semibold text-slate-700">{commaFmt(total)}</span> subscribers</div>}

          {/* Subscriber list */}
          {loading ? (
            <div className="space-y-2">{Array.from({ length: 8 }).map((_, i) => <SubscriberCardSkeleton key={i} />)}</div>
          ) : subscribers.length === 0 ? (
            <Card><EmptyState icon={CreditCard} title="No subscribers found" description="Try adjusting your filters or date range" /></Card>
          ) : (
            <>
              <div className="space-y-2">
                <AnimatePresence mode="popLayout">
                  {subscribers.map((sub, idx) => (
                    <motion.div key={sub.user_pseudo_id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} transition={{ duration: 0.12, delay: Math.min(idx * 0.015, 0.2) }}>
                      <SubscriberCard subscriber={sub} onClick={() => setSelectedSubscriber(sub)} />
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
              {totalPages > 1 && (
                <Pagination page={state.page} totalPages={totalPages} onPageChange={(p) => update({ page: p })} />
              )}
            </>
          )}
        </>
      )}
    </BuilderShell>
  );
}

// ─── KpiCard ────────────────────────────────────────────────────────────────

// KpiCard imported from shared components

// ─── SubscriberCard ─────────────────────────────────────────────────────────

function SubscriberCard({ subscriber: sub, onClick }: { subscriber: SubscriberRow; onClick: () => void }) {
  const badge = eventBadgeConfig(sub.last_event_name, sub.renewal_count);
  const isActive = sub.status === 'active';
  const hasRevenue = sub.total_revenue_try > 0;

  const acqBadges: Array<{ value: string; muted?: boolean }> = [];
  if (sub.traffic_source_name && sub.traffic_source_name !== '(direct)' && sub.traffic_source_name !== '(not set)') {
    acqBadges.push({ value: sub.traffic_source_name });
  }
  if (sub.campaign_name && sub.campaign_name !== sub.traffic_source_name) {
    acqBadges.push({ value: sub.campaign_name });
  }
  if (sub.ad_group_name) {
    acqBadges.push({ value: sub.ad_group_name, muted: true });
  }

  return (
    <Card
      interactive
      padding="none"
      className={cn('group', isActive && 'ring-1 ring-emerald-200/80 bg-emerald-50/20')}
      onClick={onClick}
    >
      <div className="px-4 py-3 space-y-2">
        {/* Row 1: Flag + Country/City | Platform + Device | Event badge */}
        <div className="flex items-center gap-3">
          <img
            src={flagUrl(sub.country)}
            alt={sub.country ?? ''}
            className="w-8 h-6 rounded-sm object-cover shadow-sm border border-slate-200/60 shrink-0"
            loading="lazy"
          />
          <div className="min-w-0">
            <span className="text-sm font-semibold text-slate-900">{sub.country ?? 'Unknown'}</span>
            {sub.city && <span className="text-xs text-slate-400 ml-1.5">{sub.city}</span>}
          </div>

          <div className="flex items-center gap-1.5 ml-1">
            {platformIconSrc(sub.platform) ? (
              <img src={platformIconSrc(sub.platform)} alt="" className="w-3.5 h-3.5 opacity-60" />
            ) : (
              <Monitor className="w-3.5 h-3.5 text-slate-400" />
            )}
            <span className="text-2xs text-slate-500">
              {sub.platform === 'IOS' ? 'iOS' : sub.platform === 'ANDROID' ? 'Android' : '-'}
            </span>
            {sub.device_brand && (
              <>
                <span className="text-slate-300">·</span>
                <span className="text-2xs text-slate-400">{sub.device_brand}</span>
              </>
            )}
            {sub.device_id && (
              <>
                <span className="text-slate-300">·</span>
                <code className="text-2xs text-slate-400 font-mono">{sub.device_id}</code>
              </>
            )}
          </div>

          <div className="ml-auto flex items-center gap-2 shrink-0">
            <Badge variant={badge.variant} dot>{badge.label}</Badge>
            <ArrowUpRight className="w-3.5 h-3.5 text-slate-300 group-hover:text-sky-500 transition-colors" />
          </div>
        </div>

        {/* Row 2: Subscription details */}
        <div className="flex items-center gap-3 text-xs">
          {sub.purchase_structure && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-indigo-50 text-indigo-700 border border-indigo-100 text-2xs font-semibold shrink-0">
              {structureLabel(sub.purchase_structure)}
            </span>
          )}

          <span className="text-slate-500">{periodLabel(sub.subscription_period)}</span>

          {hasRevenue && (
            <>
              <span className="text-slate-300">·</span>
              <span className="font-bold text-slate-900 tabular-nums">{currencyFmt(sub.total_revenue_try)}</span>
            </>
          )}

          {sub.retention_days != null && (
            <>
              <span className="text-slate-300">·</span>
              <span className="text-slate-400 tabular-nums">{sub.retention_days}d ret.</span>
            </>
          )}

          <span className="text-slate-300">·</span>
          <span className="text-slate-500 tabular-nums">{formatEventTime(sub.event_time)}</span>

          {sub.currency && (
            <>
              <span className="text-slate-300">·</span>
              <span className="text-slate-400">{sub.currency}</span>
            </>
          )}
        </div>

        {/* Row 3: Acquisition badges (only if any) */}
        {acqBadges.length > 0 && (
          <div className="flex items-center gap-1.5 flex-wrap">
            {acqBadges.map((b) => (
              <ColorChip key={b.value} value={b.value} muted={b.muted} />
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}

function SubscriberCardSkeleton() {
  return (
    <Card padding="none">
      <div className="px-4 py-3 space-y-2.5">
        <div className="flex items-center gap-3">
          <Skeleton className="w-8 h-6 rounded-sm" />
          <Skeleton className="h-4 w-28" />
          <Skeleton className="h-3 w-16" />
          <div className="ml-auto"><Skeleton className="h-5 w-16 rounded-full" /></div>
        </div>
        <div className="flex items-center gap-3">
          <Skeleton className="h-3 w-16" />
          <Skeleton className="h-3 w-14" />
          <Skeleton className="h-3 w-20" />
          <Skeleton className="h-3 w-10" />
        </div>
        <div className="flex items-center gap-1.5">
          <Skeleton className="h-5 w-20 rounded" />
          <Skeleton className="h-5 w-28 rounded" />
        </div>
      </div>
    </Card>
  );
}

// ─── SubscriberDetailPanel ──────────────────────────────────────────────────

/** Convert a Date to YYYYMMDD */
function dateToSuffix(d: Date): string {
  const yyyy = d.getFullYear().toString();
  const mm = (d.getMonth() + 1).toString().padStart(2, '0');
  const dd = d.getDate().toString().padStart(2, '0');
  return `${yyyy}${mm}${dd}`;
}

/** Get tight sinceDate/untilDate from a session start time (±1 day) */
function sessionDateRange(ts: { value: string } | string | null): { sinceDate: string; untilDate: string } | undefined {
  if (!ts) return undefined;
  const raw = typeof ts === 'object' && 'value' in ts ? ts.value : ts;
  try {
    const d = parseISO(raw);
    const before = new Date(d); before.setDate(before.getDate() - 1);
    const after = new Date(d); after.setDate(after.getDate() + 1);
    return { sinceDate: dateToSuffix(before), untilDate: dateToSuffix(after) };
  } catch {
    return undefined;
  }
}

/** Convert an ISO date string to YYYYMMDD for _TABLE_SUFFIX filter, with 1-day safety margin */
function toSinceDate(isoString: string | null): string | undefined {
  if (!isoString) return undefined;
  try {
    const d = parseISO(isoString);
    d.setDate(d.getDate() - 1);
    const yyyy = d.getFullYear().toString();
    const mm = (d.getMonth() + 1).toString().padStart(2, '0');
    const dd = d.getDate().toString().padStart(2, '0');
    return `${yyyy}${mm}${dd}`;
  } catch {
    return undefined;
  }
}

interface FirstTouchResponse {
  first_touch_time: { value: string } | string | null;
  first_open_timestamp: number | null;
}

function SubscriberDetailPanel({ subscriber: sub, onBack }: { subscriber: SubscriberRow; onBack: () => void }) {
  const [events, setEvents] = useState<PurchaseEvent[]>([]);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [sessions, setSessions] = useState<SessionRow[]>([]);
  const [sessionsLoading, setSessionsLoading] = useState(true);
  const [expandedSession, setExpandedSession] = useState<string | null>(null);
  const [sinceDate, setSinceDate] = useState<string | undefined>(undefined);
  const [sinceDateReady, setSinceDateReady] = useState(false);
  const [firstOpenTime, setFirstOpenTime] = useState<string | null>(null);

  // Step 1: Fetch first touch time from users_first_touch_intraday
  useEffect(() => {
    let cancelled = false;
    api.get<FirstTouchResponse | null>(endpoints.purchases.firstTouch(), { userPseudoId: sub.user_pseudo_id })
      .then((data) => {
        if (!cancelled) {
          // BQ TIMESTAMP comes back as { value: "..." } or plain string
          const raw = data?.first_touch_time;
          const ft = raw ? (typeof raw === 'object' && 'value' in raw ? raw.value : raw) : null;
          setFirstOpenTime(ft);
          const sd = ft ? toSinceDate(ft) : undefined;
          setSinceDate(sd);
          setSinceDateReady(true);
        }
      })
      .catch(() => {
        if (!cancelled) setSinceDateReady(true);
      });
    return () => { cancelled = true; };
  }, [sub.user_pseudo_id]);

  // Step 2: Fetch purchase events once sinceDate is resolved
  useEffect(() => {
    if (!sinceDateReady) return;
    let cancelled = false;
    setEventsLoading(true);
    const params: Record<string, string> = { userPseudoId: sub.user_pseudo_id };
    if (sinceDate) params.sinceDate = sinceDate;
    api.get<PurchaseEvent[]>(endpoints.purchases.userEvents(), params)
      .then((data) => { if (!cancelled) setEvents(data); })
      .catch(() => { if (!cancelled) toast.error('Failed to load events'); })
      .finally(() => { if (!cancelled) setEventsLoading(false); });
    return () => { cancelled = true; };
  }, [sub.user_pseudo_id, sinceDateReady, sinceDate]);

  // Step 3: Fetch sessions once sinceDate is resolved
  useEffect(() => {
    if (!sinceDateReady) return;
    let cancelled = false;
    setSessionsLoading(true);
    setSessions([]);
    const params: Record<string, string> = { userPseudoId: sub.user_pseudo_id };
    if (sinceDate) params.sinceDate = sinceDate;
    api.get<SessionRow[]>(endpoints.users.sessions(), params)
      .then((data) => { if (!cancelled) setSessions(data); })
      .catch(() => { if (!cancelled) toast.error('Failed to load sessions'); })
      .finally(() => { if (!cancelled) setSessionsLoading(false); });
    return () => { cancelled = true; };
  }, [sub.user_pseudo_id, sinceDateReady, sinceDate]);

  const badge = eventBadgeConfig(sub.last_event_name, sub.renewal_count);

  // Compute total revenue from fetched purchase events (initial_purchase + renewal only)
  const computedRevenue = useMemo(() => {
    if (eventsLoading || events.length === 0) return null;
    return events.reduce((sum, e) => {
      if ((e.event_name === 'initial_purchase' || e.event_name === 'renewal') && e.price_in_try != null && e.price_in_try > 0) {
        return sum + e.price_in_try;
      }
      return sum;
    }, 0);
  }, [events, eventsLoading]);

  const totalRevenue = computedRevenue ?? sub.total_revenue_try;
  const hasRevenue = totalRevenue > 0;

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }}>
      <Card className={cn(sub.status === 'active' && 'ring-1 ring-emerald-200/80 bg-emerald-50/20')}>
        <div className="flex items-start gap-4">
          <button onClick={onBack} className="mt-0.5 p-1.5 rounded-lg hover:bg-slate-100 transition-colors shrink-0">
            <ChevronLeft className="w-5 h-5 text-slate-500" />
          </button>
          <div className="flex-1 min-w-0 space-y-3">
            {/* Header row */}
            <div className="flex items-center gap-3 flex-wrap">
              <img src={flagUrl(sub.country)} alt={sub.country ?? ''} className="w-8 h-6 rounded-sm object-cover shadow-sm border border-slate-200/60" />
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-sm font-bold text-slate-900">{sub.country ?? 'Unknown'}</h2>
                  {sub.city && <span className="text-xs text-slate-400">{sub.city}</span>}
                </div>
                <div className="flex items-center gap-2 mt-0.5">
                  {platformIconSrc(sub.platform) ? <img src={platformIconSrc(sub.platform)} alt="" className="w-3.5 h-3.5 opacity-50" /> : <Monitor className="w-3.5 h-3.5 text-slate-400" />}
                  <span className="text-2xs text-slate-500">{sub.platform === 'IOS' ? 'iOS' : sub.platform === 'ANDROID' ? 'Android' : '-'}</span>
                  {sub.device_brand && <><span className="text-slate-300">·</span><span className="text-2xs text-slate-400">{sub.device_brand} {sub.device_model}</span></>}
                </div>
              </div>
              <div className="flex items-center gap-1.5 ml-auto">
                <Badge variant={badge.variant} dot>{badge.label}</Badge>
                {sub.is_web_to_app_user && <Badge variant="brand">Web-to-App</Badge>}
              </div>
            </div>

            {/* Info grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-2 text-2xs">
              <div><span className="text-slate-400">Pseudo ID</span><br /><code className="text-slate-600 font-mono">{sub.user_pseudo_id}</code></div>
              {sub.user_id && <div><span className="text-slate-400">User ID</span><br /><code className="text-slate-600 font-mono">{sub.user_id}</code></div>}
              <div><span className="text-slate-400">First Open</span><br /><span className="text-slate-700 font-medium">{firstOpenTime ? formatDateTime(firstOpenTime) : (sinceDateReady ? '-' : '...')}</span></div>
              <div><span className="text-slate-400">Offering</span><br /><span className="text-slate-700 font-medium">{sub.offering_id ?? '-'}</span></div>
              <div><span className="text-slate-400">Period</span><br /><span className="text-slate-700 font-medium">{periodLabel(sub.subscription_period)}</span></div>
              <div>
                <span className="text-slate-400">Total Revenue</span><br />
                {eventsLoading ? <span className="text-slate-400">...</span> : hasRevenue ? <span className="text-slate-900 font-bold">{currencyFmt(totalRevenue)}</span> : <span className="text-slate-400">-</span>}
              </div>
              {actualRenewals(sub.renewal_count) > 0 && <div><span className="text-slate-400">Renewals</span><br /><span className="text-slate-700">{actualRenewals(sub.renewal_count)}</span></div>}
              {sub.retention_days != null && <div><span className="text-slate-400">Retention</span><br /><span className="text-slate-700">{sub.retention_days}d</span></div>}
              <div><span className="text-slate-400">App Version</span><br /><span className="text-slate-700">{sub.app_version ?? '-'}</span></div>
              {sub.purchase_structure && <div><span className="text-slate-400">Purchase Origin</span><br /><span className="inline-flex items-center px-2 py-0.5 rounded-md bg-indigo-50 text-indigo-700 border border-indigo-100 text-2xs font-semibold mt-0.5">{structureLabel(sub.purchase_structure)}</span></div>}
              {sub.currency && <div><span className="text-slate-400">Currency</span><br /><span className="text-slate-700">{sub.currency}</span></div>}
            </div>

            {/* Acquisition badges */}
            {(sub.traffic_source_name || sub.campaign_name || sub.ad_group_name || sub.traffic_source) && (
              <div className="flex items-center gap-1.5 flex-wrap pt-1">
                {sub.traffic_source && <ColorChip value={sub.traffic_source} muted />}
                {sub.traffic_source_name && sub.traffic_source_name !== '(direct)' && sub.traffic_source_name !== '(not set)' && <ColorChip value={sub.traffic_source_name} />}
                {sub.campaign_name && sub.campaign_name !== sub.traffic_source_name && <ColorChip value={sub.campaign_name} />}
                {sub.ad_group_name && <ColorChip value={sub.ad_group_name} muted />}
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* Purchase Events */}
      <div className="mt-4">
        <div className="flex items-center gap-2 mb-3">
          <Activity className="w-4 h-4 text-slate-400" />
          <h3 className="text-sm font-semibold text-slate-900">Purchase Events {!eventsLoading && `(${events.length})`}</h3>
        </div>
        {eventsLoading ? (
          <div className="space-y-2">{Array.from({ length: 3 }).map((_, i) => (
            <Card key={i} padding="sm"><div className="flex items-center gap-4"><Skeleton className="w-3 h-3 rounded-full" /><Skeleton className="h-4 w-32" /><Skeleton className="h-4 w-20" /></div></Card>
          ))}</div>
        ) : events.length === 0 ? (
          <Card><EmptyState icon={Activity} title="No events found" description="No purchase events found for this user" /></Card>
        ) : (
          <Card padding="none">
            <div className="divide-y divide-slate-100">
              {events.map((event, i) => <PurchaseEventRow key={`${event.event_name}-${event.event_timestamp}-${i}`} event={event} />)}
            </div>
          </Card>
        )}
      </div>

      {/* Sessions */}
      <div className="mt-4">
        <div className="flex items-center gap-2 mb-3">
          <Signal className="w-4 h-4 text-slate-400" />
          <h3 className="text-sm font-semibold text-slate-900">Sessions {!sessionsLoading && `(${sessions.length})`}</h3>
        </div>
        {sessionsLoading ? (
          <div className="space-y-2">{Array.from({ length: 3 }).map((_, i) => (
            <Card key={i} padding="sm"><div className="flex items-center gap-4"><Skeleton className="h-4 w-4 rounded" /><Skeleton className="h-4 w-32" /><Skeleton className="h-4 w-20" /><Skeleton className="h-4 w-16 ml-auto" /></div></Card>
          ))}</div>
        ) : sessions.length === 0 ? (
          <Card><EmptyState icon={Signal} title="No sessions found" description="This user has no session data" /></Card>
        ) : (
          <div className="space-y-2">
            <AnimatePresence initial={false}>
              {sessions.map((session, idx) => (
                <SessionAccordion
                  key={session.event_session_id}
                  session={session}
                  index={idx}
                  isExpanded={expandedSession === session.event_session_id}
                  onToggle={() => setExpandedSession((prev) => prev === session.event_session_id ? null : session.event_session_id)}
                  userPseudoId={sub.user_pseudo_id}
                  sinceDate={sinceDate}
                />
              ))}
            </AnimatePresence>
          </div>
        )}
      </div>
    </motion.div>
  );
}

// ─── PurchaseEventRow ───────────────────────────────────────────────────────

const EVENT_ICONS: Record<string, React.ReactNode> = {
  initial_purchase: <CreditCard className="w-3.5 h-3.5 text-emerald-600" />,
  renewal: <RefreshCw className="w-3.5 h-3.5 text-sky-600" />,
  cancellation: <XCircle className="w-3.5 h-3.5 text-amber-600" />,
  expiration: <Timer className="w-3.5 h-3.5 text-red-500" />,
  billing_issue: <ShieldAlert className="w-3.5 h-3.5 text-red-600" />,
  subscription_extended: <TrendingUp className="w-3.5 h-3.5 text-violet-600" />,
};

const EVENT_BG: Record<string, string> = {
  initial_purchase: 'bg-emerald-50',
  renewal: 'bg-sky-50',
  cancellation: 'bg-amber-50',
  expiration: 'bg-red-50',
  billing_issue: 'bg-red-50',
  subscription_extended: 'bg-violet-50',
};

function PurchaseEventRow({ event }: { event: PurchaseEvent }) {
  const time = event.event_time
    ? (() => { try { return format(parseISO(event.event_time), 'MMM d, HH:mm:ss'); } catch { return event.event_time; } })()
    : '-';

  return (
    <div className={cn('flex items-center gap-4 px-4 py-3', EVENT_BG[event.event_name] ?? '')}>
      <div className="w-6 flex justify-center shrink-0">{EVENT_ICONS[event.event_name] ?? <Activity className="w-3.5 h-3.5 text-slate-400" />}</div>
      <div className="w-40 shrink-0"><span className="text-xs font-semibold text-slate-900">{event.event_name.replace(/_/g, ' ')}</span></div>
      <div className="text-2xs text-slate-500 tabular-nums w-36 shrink-0">{time}</div>

      <div className="text-xs font-bold text-slate-900 tabular-nums w-24 shrink-0 text-right">
        {event.price_in_try != null && event.price_in_try > 0 ? currencyFmt(event.price_in_try) : ''}
      </div>

      <div className="flex items-center gap-3 flex-1 min-w-0 text-2xs text-slate-500">
        {event.subscription_period && <span>{periodLabel(event.subscription_period)}</span>}
        {event.event_name !== 'initial_purchase' && event.renewal_count != null && actualRenewals(event.renewal_count) > 0 && (
          <span>renewal #{actualRenewals(event.renewal_count)}</span>
        )}
        {event.purchase_structure && <span className="text-slate-400">{structureLabel(event.purchase_structure)}</span>}
      </div>
    </div>
  );
}

// ─── Session Accordion ──────────────────────────────────────────────────────

function SessionAccordion({
  session,
  index,
  isExpanded,
  onToggle,
  userPseudoId,
  sinceDate,
}: {
  session: SessionRow;
  index: number;
  isExpanded: boolean;
  onToggle: () => void;
  userPseudoId: string;
  sinceDate?: string;
}) {
  const [events, setEvents] = useState<SessionEvent[]>([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventsFetched, setEventsFetched] = useState(false);

  // Lazy-load events on first expand — use tight date range from session start time
  const sessionRange = sessionDateRange(session.session_start_time);

  useEffect(() => {
    if (!isExpanded || eventsFetched) return;
    let cancelled = false;
    setEventsLoading(true);

    const params: Record<string, string> = {
      userPseudoId,
      sessionId: session.event_session_id,
    };
    if (sessionRange) {
      params.sinceDate = sessionRange.sinceDate;
      params.untilDate = sessionRange.untilDate;
    } else if (sinceDate) {
      params.sinceDate = sinceDate;
    }

    api
      .get<SessionEvent[]>(endpoints.users.sessionEvents(), params)
      .then((data) => {
        if (!cancelled) { setEvents(data); setEventsFetched(true); }
      })
      .catch(() => { if (!cancelled) toast.error('Failed to load session events'); })
      .finally(() => { if (!cancelled) setEventsLoading(false); });

    return () => { cancelled = true; };
  }, [isExpanded, eventsFetched, userPseudoId, session.event_session_id, sessionRange, sinceDate]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.12, delay: Math.min(index * 0.02, 0.15) }}
    >
      <Card padding="none" className={cn(isExpanded && 'ring-1 ring-sky-200')}>
        {/* Session header — always visible */}
        <button
          onClick={onToggle}
          className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-50/60 transition-colors"
        >
          <motion.div animate={{ rotate: isExpanded ? 90 : 0 }} transition={{ duration: 0.15 }}>
            <ChevronRight className="w-4 h-4 text-slate-400" />
          </motion.div>

          <span className="text-2xs font-medium text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded tabular-nums">
            #{index + 1}
          </span>

          <span className="text-xs font-mono text-slate-600">
            {session.event_session_id}
          </span>

          <div className="flex items-center gap-4 ml-auto shrink-0 text-2xs text-slate-500">
            <span className="flex items-center gap-1 tabular-nums">
              <Clock className="w-3 h-3 text-slate-400" />
              {parseBqDateTime(session.session_start_time)}
            </span>
            <span className="flex items-center gap-1 tabular-nums">
              <Activity className="w-3 h-3 text-slate-400" />
              {formatSessionDuration(session.session_duration_seconds)}
            </span>
            <span className="flex items-center gap-1">
              <Layers className="w-3 h-3 text-slate-400" />
              {session.event_count} events
            </span>
            {session.session_start_event_name && (
              <Badge variant="default" className="text-2xs">
                {session.session_start_event_name}
              </Badge>
            )}
          </div>
        </button>

        {/* Expanded events */}
        <AnimatePresence initial={false}>
          {isExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2, ease: [0.23, 1, 0.32, 1] }}
              className="overflow-hidden"
            >
              <div className="border-t border-slate-100 px-4 py-3">
                {eventsLoading ? (
                  <div className="space-y-2 py-2">
                    {Array.from({ length: 4 }).map((_, i) => (
                      <div key={i} className="flex items-center gap-3">
                        <Skeleton className="w-2 h-2 rounded-full" />
                        <Skeleton className="h-3.5 w-16" />
                        <Skeleton className="h-3.5 w-32" />
                      </div>
                    ))}
                  </div>
                ) : events.length === 0 ? (
                  <p className="text-xs text-slate-400 py-2">No events found for this session</p>
                ) : (
                  <div className="relative">
                    <div className="absolute left-[5px] top-2 bottom-2 w-px bg-slate-200" />
                    <div className="space-y-0">
                      {events.map((event, i) => (
                        <SessionEventRow key={`${event.event_name}-${event.event_timestamp}-${i}`} event={event} index={i} />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </Card>
    </motion.div>
  );
}

// ─── Session Event Row ──────────────────────────────────────────────────────

function SessionEventRow({ event, index }: { event: SessionEvent; index: number }) {
  const [showParams, setShowParams] = useState(false);
  const color = getSessionEventColor(event.event_name);

  const meaningfulParams = useMemo(() => {
    if (!event.params) return [];
    return event.params.filter(
      (p) => !['event_session_id', 'session_start_time', 'ga_session_id', 'ga_session_number', 'engaged_session_event'].includes(p.key),
    );
  }, [event.params]);

  return (
    <motion.div
      initial={{ opacity: 0, x: -4 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.1, delay: Math.min(index * 0.01, 0.3) }}
      className="relative pl-6 py-1.5 group"
    >
      {/* Timeline dot */}
      <div
        className="absolute left-[2px] top-[11px] w-[7px] h-[7px] rounded-full border-2 border-white"
        style={{ backgroundColor: color }}
      />

      <div className="flex items-center gap-2 min-w-0">
        <span className="text-2xs text-slate-400 tabular-nums w-14 shrink-0">
          {parseBqTimestamp(event.event_time)}
        </span>

        <span
          className="text-xs font-medium px-1.5 py-0.5 rounded"
          style={{ color, backgroundColor: `${color}12` }}
        >
          {event.event_name}
        </span>

        {meaningfulParams.length > 0 && (
          <button
            onClick={() => setShowParams(!showParams)}
            className="text-2xs text-slate-400 hover:text-slate-600 transition-colors flex items-center gap-0.5"
          >
            <span>{meaningfulParams.length} params</span>
            <ChevronDown className={cn('w-3 h-3 transition-transform', showParams && 'rotate-180')} />
          </button>
        )}
      </div>

      <AnimatePresence>
        {showParams && meaningfulParams.length > 0 && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="overflow-hidden"
          >
            <div className="mt-1.5 ml-14 bg-slate-50 rounded-lg px-3 py-2 space-y-1">
              {meaningfulParams.map((p) => {
                const val = p.string_value ?? p.int_value ?? p.double_value ?? '—';
                return (
                  <div key={p.key} className="flex items-baseline gap-2 text-2xs">
                    <span className="text-slate-400 font-mono shrink-0">{p.key}</span>
                    <span className="text-slate-300">=</span>
                    <span className="text-slate-700 font-mono break-all">{String(val)}</span>
                  </div>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
